import type { Conversation, UserOptions } from 'talkjs/types/talk.types';
import type { Message } from '../inbox/api/talkjs/webhook.types';

export enum TalkJsRoles {
  Admin = 'admin',
  Couple_UK = 'couple',
  Couple_DE = 'couple_DE',
  Couple_FR = 'couple_FR',
  Couple_IE = 'couple_IE',
  Couple_PL = 'couple_PL',
  Couple_Global = 'couple_global',
  Supplier_UK = 'supplier',
  Supplier_DE = 'supplier_DE',
  Supplier_FR = 'supplier_FR',
  Supplier_IE = 'supplier_IE',
  Supplier_PL = 'supplier_PL',
  Supplier_Global = 'supplier_global',
  Couple_External_UK = 'external_couple',
  Couple_External_DE = 'external_couple_DE',
  Couple_External_FR = 'external_couple_FR',
  Couple_External_IE = 'external_couple_IE',
  Couple_External_PL = 'external_couple_PL',
  Couple_External_Global = 'external_couple_global',
}

export interface ChatUserOptions extends NonNullable<UserOptions> {
  id: string;
  role: TalkJsRoles;
}

export interface CoupleChatUserOptions extends ChatUserOptions {
  custom: {
    userId: string;
  };
}

export interface SupplierChatUserOptions extends ChatUserOptions {
  custom: {
    userId: string;
    publicId: string;
    type: string;
    city: string;
    adminArea0: string;
    adminArea1: string;
    country: string;
    photoUrl: string;
    seoUrlSlug: string;
  };
}

/**
 * Object used to create conversation via REST API
 * @see: https://talkjs.com/dashboard/tI9iW5Fu/docs/Reference/REST_API/Conversations.html
 */
export interface ConversationOptions extends Omit<Conversation, 'participants'> {
  participants: string[];
  custom: {
    // it would be more natural to use a boolean here, but talkjs only accepts string or null
    // for custom properties: https://talkjs.com/docs/Reference/Concepts/Conversations/#custom
    archivedByCouple?: 'true' | null;
    repliedByCouple?: string; // Timestamp of the last reply by couple (excluding initial message)
    repliedBySupplier?: string; // Timestamp of the last reply by supplier
    type?: ConversationCustomType | null;
    /* @deprecated - only in old conversations */
    supplierWeddingId?: string;
    /* @deprecated - only in old conversations, enquiryId was supposed to be supplierWeddingId */
    enquiryId?: string;
  };
}

/**
 * @see https://talkjs.com/docs/Reference/REST_API/Messages/#sending-on-behalf-of-a-user
 */
export interface CustomMessageBody {
  text?: Message['text'];
  type?: Message['type'];
  sender?: string;
  attachmentToken?: string;
  referencedMessageId?: string;
  custom?: Message['custom'];
  idempotencyKey?: string;
}

export type ConversationListFilters = {
  archivedByCouple?: boolean;
  startingAfter?: string;
  limit: number;
};

/**
 * Custom types help distinguish conversations other than standard supplier-couple in analytics or customer.io
 * WARNING: Be careful when changing the values because they are used externally (e.g. in Customer.io campaigns)
 */
export enum ConversationCustomType {
  MessageCenter = 'message-center', // Conversation with Bridebook (Notifications)
}

/**
 * Options for synchronising (creating or updating) a conversation in TalkJS.
 */
export interface SyncConversationOptions {
  userOptions: ChatUserOptions[];
  // Welcome messages to show up at the top of the conversation
  welcomeMessages?: string[];
  // Regular messages to send to the conversation
  messages?: CustomMessageBody[];
  // Custom conversation properties
  custom?: Partial<ConversationOptions['custom']>;
  // When true, sends messages via the Pub/Sub queue to avoid TalkJS rate limits
  queueMessages?: boolean;
}
